import { Card, Tabs, TabType } from '@clickcease/cc-client-components-lib'
import React, { useMemo, useState } from 'react'
import { InvalidVisitsTrendsAndThreatGroupsStyled } from './InvalidVisitsTrendsAndThreatGroups.styled'
import { useTranslation } from 'react-i18next'
import { TrendsAndThreatGroups } from 'Components/Elements/Chart/TrendsAndThreatGroups'

import { chartConfig } from './InvalidVisitsTrendsAndThreatGroups.utils'
import { useGetPlansDetails } from 'Features/accountSettings/api/getPlansDetails'
import { PRODUCT_TYPES } from 'Types/ProductTypes'
import { TrendsData } from 'Features/home/api/types'
import { NewTrendsAndThreatGroups } from 'Components/Elements/Chart/TrendsAndThreatGroups/NewTrendsAndThreatGroups'
import { useTrends } from 'Features/home/api/getTrends'
import { useThreatGroups } from 'Features/home/api/getThreatGroups'
import { useUserStore } from 'Stores/userStoreModuleFed'
import { reduceBotzappingData } from 'Features/home/routes/Home/utils'
import { DATE_FORMATS, getCountryValueByCountryLabel, toUnixDateRangeNew } from 'Utils/dates'
import { startOfToday, subWeeks, format } from 'date-fns'

import { useGetBotMitigationData } from 'Features/home/api/getProductData'

export const NewInvalidVisitsTrendsAndThreatGroups = () => {
  const { t } = useTranslation()
  const [activeTab, setActiveTab] = useState<TabType>()

  const { data: botMitigationData, isLoading: bmDataIsLoading } = useGetBotMitigationData({
    groupByDomain: false,
  })

  const botzappingData = useMemo(
    () =>
      reduceBotzappingData({
        datedSummary: botMitigationData?.datedSummaries,
        insights: botMitigationData?.insights,
      }),
    [botMitigationData]
  )

  const { timezoneCountry } = useUserStore()

  const selectedTimezone = useMemo(() => {
    return getCountryValueByCountryLabel(timezoneCountry)
  }, [timezoneCountry])

  const today = startOfToday()
  const dateRange = {
    from: subWeeks(today, 1),
    to: today,
  }

  const serverDateConversion = toUnixDateRangeNew(timezoneCountry, dateRange)

  const { data: trendsData, isLoading: trendsAreLoading } = useTrends({
    domains: [],
    campaigns: [],
    platforms: [],
    startDate: serverDateConversion?.from,
    endDate: serverDateConversion?.to,
    timezone: selectedTimezone,
  })

  const { data: threatGroupsData, isLoading: threatGroupsAreLoading } = useThreatGroups({
    domains: [],
    campaigns: [],
    platforms: [],
    startDate: serverDateConversion?.from,
    endDate: serverDateConversion?.to,
    timezone: selectedTimezone,
  })

  const isLoading = trendsAreLoading || threatGroupsAreLoading || bmDataIsLoading

  const { data: planDetails } = useGetPlansDetails()

  const trendsVisitsAndInvalidRate = trendsData
    ? trendsData.map((item: TrendsData) => ({
        x: item.timestamp,
        VISITS: item.invalid,
        INVALID_RATE: item.invalidRate * 100,
      }))
    : []

  const totalVisits = trendsVisitsAndInvalidRate.map(
    (item) => [item.x * 1000, item.VISITS] as [number, number]
  )
  const totalInvalidRates = trendsVisitsAndInvalidRate.map(
    (item) => [item.x * 1000, item.INVALID_RATE] as [number, number]
  )

  const tabs: TabType[] | undefined = useMemo(() => {
    const paidMarketingTab = {
      id: 1,
      title: t('pending_domain_table.paid_marketing', { ns: 'dashboard' }),
      page: (
        <NewTrendsAndThreatGroups
          {...chartConfig(!!trendsVisitsAndInvalidRate.length, true)}
          areaSPLineData={[
            {
              name: t('total_invalid_visits', { ns: 'home' }),
              data: totalVisits,
            },
            {
              name: t('total_invalid_rate', { ns: 'home' }),
              data: totalInvalidRates,
              percentInTitle: true,
            },
          ]}
          threatGroupsData={threatGroupsData || []}
          AreaSPLineOptions={{
            xAxis: {
              tickInterval: 3600 * 30000,
              type: 'datetime',
              labels: {
                formatter: ({ value }) => {
                  return format(value as number, DATE_FORMATS.MONTH_DAY_FORMAT)
                },
              },
              title: {
                text: '',
              },
              gridLineWidth: 1,
              gridLineDashStyle: 'Dash',
            },
          }}
        />
      ),
    }

    const botZappingTab = {
      id: 2,
      title: t('pending_domain_table.bot_zapping', { ns: 'dashboard' }),
      page: botzappingData && (
        <TrendsAndThreatGroups
          {...chartConfig(!!botzappingData.threatGroupsBreakdown.data.length)}
          threatGroupsBreakdown={botzappingData.threatGroupsBreakdown}
          areaSPLineData={[
            {
              name: t('total_invalid_visits', { ns: 'home' }),
              data: botzappingData.VISITS.map(([first, second]) => [first * 1000, second]),
            },
            {
              name: t('total_invalid_rate', { ns: 'home' }),
              data: botzappingData.INVALID_RATE.map(([first, second]) => [first * 1000, second]),
            },
          ]}
        />
      ),
    }

    const result: TabType[] = []

    if (planDetails && planDetails.plans) {
      const hasPaidMarketing = planDetails.plans.some(
        (product) => product.product.toLowerCase() === PRODUCT_TYPES.PAID_MARKETING
      )
      const hasBotZapping = planDetails.plans.some(
        (product) => product.product.toLowerCase() === PRODUCT_TYPES.BOT_ZAPPING
      )

      if (hasPaidMarketing) {
        result.push(paidMarketingTab)
      }

      if (hasBotZapping) {
        result.push(botZappingTab)
      }

      if ((hasPaidMarketing && hasBotZapping) || (hasPaidMarketing && !hasBotZapping)) {
        setActiveTab(paidMarketingTab)
      } else if (hasBotZapping) {
        setActiveTab(botZappingTab)
      }
    }

    return result
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [botzappingData, isLoading, planDetails, threatGroupsData])

  const showTabs = !!(!isLoading && botzappingData && tabs && activeTab)

  return (
    <Card
      id={'home-invalid-visits-threat-group-card'}
      title={t('invalid_visits_trends_threat_groups.title', { ns: 'dashboard' })}
      isLoading={isLoading}
      headerUtilities={
        <InvalidVisitsTrendsAndThreatGroupsStyled.HeaderUtilities>
          <InvalidVisitsTrendsAndThreatGroupsStyled.HeaderUtilitiesTitleWrapper>
            {t('show_data', { ns: 'dashboard' })}
          </InvalidVisitsTrendsAndThreatGroupsStyled.HeaderUtilitiesTitleWrapper>
        </InvalidVisitsTrendsAndThreatGroupsStyled.HeaderUtilities>
      }
    >
      <InvalidVisitsTrendsAndThreatGroupsStyled.Wrapper>
        {showTabs && <Tabs tabs={tabs} activeTab={activeTab} onClick={setActiveTab} />}
      </InvalidVisitsTrendsAndThreatGroupsStyled.Wrapper>
    </Card>
  )
}
